import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useUpsertDeveloper, type Developer } from './useInventory';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Present when editing; absent when creating. */
  developer?: Developer | undefined;
}

/** Developer create/edit (#19). Writes go through admin_upsert_developer(), never a
 *  direct table write — `developers` has no INSERT or UPDATE policy at all. */
export function DeveloperDialog({ open, onClose, developer }: Props) {
  const { t } = useTranslation();
  const upsert = useUpsertDeveloper();

  const [name, setName] = useState('');
  const [areas, setAreas] = useState('');
  const [commission, setCommission] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever the dialog opens so an edit never shows the previous row's
  // values for a moment.
  useEffect(() => {
    if (!open) return;
    setName(developer?.name ?? '');
    setAreas((developer?.areas ?? []).join('، '));
    setCommission(developer?.commission_percent ?? '');
    setContactName(developer?.contact_name ?? '');
    setContactPhone(developer?.contact_phone ?? '');
    setError(null);
  }, [open, developer]);

  const submit = async () => {
    if (!name.trim()) {
      setError(t('inventory.nameRequired'));
      return;
    }
    try {
      await upsert.mutateAsync({
        id: developer?.id ?? null,
        name: name.trim(),
        // Both the Arabic comma and the Latin one, because staff type whichever
        // their keyboard is on.
        areas: areas
          .split(/[,،]/)
          .map((a) => a.trim())
          .filter(Boolean),
        commissionPercent: commission.trim() ? Number(commission) : null,
        contactName: contactName.trim() || null,
        contactPhone: contactPhone.trim() || null,
      });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal
      open={open}
      title={developer ? t('inventory.editDeveloper') : t('inventory.addDeveloper')}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Input label={t('inventory.name')} value={name} onChange={(e) => setName(e.target.value)} />
        <Input
          label={t('inventory.areas')}
          hint={t('inventory.areasHint')}
          value={areas}
          onChange={(e) => setAreas(e.target.value)}
        />
        <Input
          label={t('inventory.commission')}
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={commission}
          onChange={(e) => setCommission(e.target.value)}
        />
        <Input
          label={t('inventory.contactName')}
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
        />
        <Input
          label={t('inventory.contactPhone')}
          dir="ltr"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
        />

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={upsert.isPending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
